import { ItemDetailSize } from "@redotech/redo-customer-portal-shared/util";
import { Order } from "@redotech/redo-model/order";
import {
  ReturnStatus,
  isReturnStatusDisplayedRejected,
} from "@redotech/redo-model/return-status";
import { LineItem } from "@redotech/redo-model/shopify-order";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import {
  RedoCommandMenu,
  RedoCommandMenuItem,
} from "@redotech/redo-web/arbiter-components/command-menu/redo-command-menu";
import { RedoSearchTextInput } from "@redotech/redo-web/arbiter-components/input/redo-search-text-input";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import CheckCircleIcon from "@redotech/redo-web/arbiter-icon/check-circle.svg";
import ChevronDownSvg from "@redotech/redo-web/arbiter-icon/chevron-down.svg";
import ClockIcon from "@redotech/redo-web/arbiter-icon/clock.svg";
import DotIcon from "@redotech/redo-web/arbiter-icon/dot-01.svg";
import SearchIcon from "@redotech/redo-web/arbiter-icon/search-sm_filled.svg";
import XCircleIcon from "@redotech/redo-web/arbiter-icon/x-circle.svg";
import { Flex } from "@redotech/redo-web/flex";
import {
  AnimatedSkeletonBlock,
  SkeletonText,
} from "@redotech/redo-web/skeleton";
import { Text } from "@redotech/redo-web/text";
import {
  Fragment,
  memo,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { SettingsContext } from "../../contexts/settings";
import { ItemDetail } from "../shared-components/item-detail";
import { ViewAllOrdersButton } from "../shared-components/view-all-orders-button";
import { useLineItemRow, useOrderCard, useOrders } from "./hooks/use-orders";
import * as ordersStyles from "./orders.module.css";
import { useEmailAuth, useShowAllOrdersButtonLoad } from "./utils";

// Make interface match the one in use-order-returns.ts
interface ReturnedLineItem extends LineItem {
  _returnId?: string;
  _returnedItem?: boolean;
}

// Helper function to check if claims are enabled in settings
function useClaimsEnabled() {
  const settings = useContext(SettingsContext);
  return (
    settings?.packageProtection?.enabled === true &&
    settings?.packageProtection?.customerClaimsEnabled === true
  );
}

// Helper function to check if registrations are enabled in settings
function useRegistrationsEnabled() {
  const settings = useContext(SettingsContext);
  return settings?.warranties?.registrationEnabled === true;
}

// Helper function to check if warranties are enabled in settings
function useWarrantiesEnabled() {
  const settings = useContext(SettingsContext);
  return settings?.warranties?.enabled === true;
}

export const Orders = memo(function Orders({
  registerOnly,
}: {
  registerOnly: boolean;
}) {
  const {
    ordersLoading,
    totalOrders,
    totalSearchResults,
    filteredOrders,
    isMobile,
    searchString,
    timeFrameOptions,
    selectedTimeFrameOption,
    setSearchString,
    handleOptionSelected,
    handleRetailRegisterClick,
    hasMoreOrders,
    loadMoreOrders,
    handleDifferentEmailClick,
    text,
    ordersError,
    shouldShowLoginAgainButton,
  } = useOrders({ registerOnly });

  const showAllOrdersButton = useShowAllOrdersButtonLoad();
  const { emailSent, showRetry, triggerSendEmail, sendEmailError } =
    useEmailAuth();
  const claimsEnabled = useClaimsEnabled();
  const registrationsEnabled = useRegistrationsEnabled();

  // Ref to hold the observer instance
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Create a ref for the loader element
  const observerTarget = useCallback(
    (node: HTMLDivElement | null) => {
      // Clean up previous observer if it exists
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }

      // If no node, just return (cleanup case)
      if (!node) return;

      // Create new IntersectionObserver
      observerRef.current = new IntersectionObserver(
        (entries) => {
          // If the target element is visible and we have more orders to load
          if (entries[0].isIntersecting && hasMoreOrders && !ordersLoading) {
            loadMoreOrders();
          }
        },
        {
          root: null,
          rootMargin: "200px", // Increased margin to load earlier
          threshold: 0.1,
        },
      );

      // Start observing the node
      observerRef.current.observe(node);
    },
    [hasMoreOrders, ordersLoading, loadMoreOrders],
  );

  // Cleanup observer on unmount
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, []);

  // Determine if we're in an initial loading state with no orders
  const isInitialLoad = ordersLoading && filteredOrders.length === 0;

  // Determine if we're loading more orders (not initial load)
  const isLoadingMore = ordersLoading && filteredOrders.length > 0;

  // Prepare the actions text for the header
  const actions: string[] = ["Start a return"];
  if (claimsEnabled) actions.push("file a claim");
  if (registrationsEnabled) actions.push("register your product");
  const actionsText = actions.join(", ").replace(/, ([^,]*)$/, ", or $1");

  return (
    <Flex
      align="center"
      bgColor="primary"
      flex={1}
      flexDirection="column"
      id="page"
      justify="flex-start"
      overflow="hidden"
      p="xxs"
      position="relative"
    >
      <Flex
        flexDirection="column"
        gap={isMobile ? "none" : "5xl"}
        justify="flex-start"
        maxW="full"
        minW="none"
        mx={isMobile ? "xl" : "sm"}
        my={isMobile ? "3xl" : "5xl"}
      >
        <Flex flexDirection="column" gap="2xl" pb={isMobile ? "xl" : "none"}>
          <Flex flexDirection="column" gap="sm">
            <Flex align="center" flexDirection="row" justify="space-between">
              <Text fontSize="2xl" fontWeight="semibold">
                {text.header}
              </Text>
            </Flex>
            <Text fontSize="sm" fontWeight="regular" textColor="tertiary">
              {actionsText}
              {registrationsEnabled && (
                <>
                  . For orders purchased from a retailer,{" "}
                  <span
                    onClick={handleRetailRegisterClick}
                    style={{ textDecoration: "underline", cursor: "pointer" }}
                  >
                    click here
                  </span>{" "}
                  to register your product
                </>
              )}
              .
            </Text>
          </Flex>
          <Flex
            flexDirection={isMobile ? "column-reverse" : "row"}
            gap={isMobile ? "2xl" : "md"}
            justify="space-between"
            w="full"
          >
            <Flex align="center" flexDirection="row" gap="sm">
              <Flex>
                <RedoSingleSelectDropdownInput<Temporal.Duration>
                  options={timeFrameOptions}
                  optionSelected={handleOptionSelected}
                  selectedItem={selectedTimeFrameOption}
                />
              </Flex>
              {ordersLoading ? (
                <SkeletonText length={15} />
              ) : (
                <Text fontSize="xs" fontWeight="regular" textColor="tertiary">
                  Showing{" "}
                  <Text as="span" fontWeight="semibold" textColor="tertiary">
                    {totalOrders} results
                  </Text>
                </Text>
              )}
            </Flex>
            <Flex w={isMobile ? "full" : "fit-content"}>
              <RedoSearchTextInput
                // TODO: Make these actions optional
                currentSearchIndex={0}
                downAction={() => {}}
                searchString={searchString}
                setSearchString={setSearchString}
                textInputProps={{
                  IconLeading: SearchIcon,
                  placeholder: text.searchPlaceholder,
                  setValue: setSearchString,
                  value: searchString,
                  fullWidth: isMobile,
                }}
                totalSearchResults={totalSearchResults}
                upAction={() => {}}
              />
            </Flex>
          </Flex>
        </Flex>
        {/* Show initial loading skeleton */}
        {isInitialLoad && (
          <>
            {Array.from({ length: 3 }).map((_, index) => (
              <Flex key={`skeleton-${index}`} my="md">
                <AnimatedSkeletonBlock height="125px" width="100%" />
              </Flex>
            ))}
          </>
        )}

        {/* Show actual orders */}
        {filteredOrders.map((order, index) =>
          isMobile ? (
            <Fragment key={order.shopify.id}>
              <div className={ordersStyles.mobileDivider} />
              <MobileOrderCard order={order} registerOnly={registerOnly} />
            </Fragment>
          ) : (
            <OrderCard
              key={order.shopify.id}
              order={order}
              registerOnly={registerOnly}
            />
          ),
        )}

        {/* Load more indicator */}
        {hasMoreOrders && filteredOrders.length > 0 && (
          <div
            ref={observerTarget}
            style={{ height: "20px", margin: "20px 0" }}
          >
            {isLoadingMore && (
              <Flex align="center" justify="center" my="lg">
                <AnimatedSkeletonBlock height="4px" width="40%" />
              </Flex>
            )}
          </div>
        )}

        {/* Empty state */}
        {filteredOrders.length === 0 && !isInitialLoad && (
          <Flex
            align="center"
            flexDirection="column"
            gap="2xl"
            justify="center"
            my="5xl"
          >
            <Text fontSize="md" fontWeight="medium" textColor="tertiary">
              No orders found
            </Text>
            {ordersError && (
              <Text fontSize="sm" fontWeight="regular" textColor="error">
                {ordersError}
              </Text>
            )}
            {shouldShowLoginAgainButton && (
              <RedoButton
                hierarchy="secondary"
                onClick={handleDifferentEmailClick}
                text="Can't find your order? Log in again"
              />
            )}
          </Flex>
        )}

        {showAllOrdersButton.value && (
          <Flex justify="center" mt={isMobile ? "3xl" : "2xl"}>
            <ViewAllOrdersButton
              emailSent={emailSent}
              sendEmailError={sendEmailError}
              showRetry={showRetry}
              triggerSendEmail={triggerSendEmail}
            />
          </Flex>
        )}
      </Flex>
    </Flex>
  );
});

const MobileOrderCard = memo(function MobileOrderCard({
  order,
  registerOnly = false,
}: {
  order: Order;
  registerOnly?: boolean;
}) {
  const [claimMenuOpen, setClaimMenuOpen] = useState(false);
  const [claimMenuRef, setClaimMenuRef] = useState<HTMLButtonElement | null>(
    null,
  );

  // Get feature flags from hooks
  const registrationsEnabled = useRegistrationsEnabled();
  const claimsEnabled = useClaimsEnabled();
  const warrantiesEnabled = useWarrantiesEnabled();

  const {
    orderName,
    formattedOrderDate,
    orderItems,
    orderSourceDisplayName,
    hideRegisterButton,
    shouldDisableButtons,
    findTotalRegistrationsByLineItemId,
    isLineItemEligibleForRegistration,
    handleRegisterClick,
    handleViewOrderDetailsClick,
    handleStartReturnClick,
    handleStartClaimClick,
    handleStartWarrantyClaimClick,
    handleViewReturnDetailsClick,
    text,
    getReturnCompletionMessage,
    getReturnHeaderText,
    separateLineItems,
    getReturnsByReturnId,
    getReturnedItemsByReturnId,
    getReturnableQuantity,
    returnsLoading,
  } = useOrderCard({ order });

  // Define claim menu items
  const claimMenuItems: RedoCommandMenuItem[] = [
    {
      text: text.startClaimButton,
      onClick: handleStartClaimClick,
      disabled: shouldDisableButtons,
    },
    {
      text: text.startWarrantyClaimButton,
      onClick: handleStartWarrantyClaimClick,
      disabled: shouldDisableButtons,
    },
  ];

  // Group line items by return ID
  const returnsByReturnId = getReturnsByReturnId();

  // Separate returnable from fully returned items
  // Type assertion is needed because there's a mismatch between the LineItem types from different imports
  // The orderItems from useOrderCard has a different LineItem type than what separateLineItems expects
  const mobileOrderItemsData = separateLineItems(orderItems as LineItem[]);
  const returnableItems = mobileOrderItemsData.returnableItems;

  // Get returned items grouped by return ID
  const returnedItemsByReturnId = getReturnedItemsByReturnId(
    orderItems as LineItem[],
  );

  // Handle click on return details
  const handleReturnDetailsClick = (returnId: string) => () => {
    handleViewReturnDetailsClick(returnId);
  };

  return (
    <Flex
      className={ordersStyles.mobileOrderCard}
      flexDirection="column"
      gap="3xl"
      py="xl"
    >
      <Flex flexDirection="column" gap="xl">
        <Flex flexDirection="column" gap="none">
          <Text fontSize="lg" fontWeight="semibold">
            Order {orderName}
          </Text>
          <Flex align="center" flexDirection="row" gap="xs">
            {orderSourceDisplayName && (
              <>
                <Text fontSize="xs" fontWeight="regular" textColor="tertiary">
                  {orderSourceDisplayName}
                </Text>
                <DotIcon className={ordersStyles.dotIcon} />
              </>
            )}
            <Text fontSize="xs" fontWeight="regular" textColor="tertiary">
              {formattedOrderDate}
            </Text>
            <DotIcon className={ordersStyles.dotIcon} />
            <Text
              className={ordersStyles.orderDetailsLink}
              fontSize="xs"
              fontWeight="regular"
              onClick={handleViewOrderDetailsClick}
              style={{ textDecoration: "underline", cursor: "pointer" }}
            >
              {text.viewOrderDetails}
            </Text>
          </Flex>
        </Flex>

        <Flex flexDirection="column" gap="sm">
          {returnsLoading ? (
            <RedoButton
              disabled
              hierarchy="primary"
              size="md"
              text="Loading returns..."
            />
          ) : registerOnly ? (
            !hideRegisterButton &&
            registrationsEnabled && (
              <RedoButton
                hierarchy="secondary"
                onClick={handleRegisterClick}
                size="md"
                text={text.registerButton}
              />
            )
          ) : (
            <>
              <RedoButton
                disabled={shouldDisableButtons}
                hierarchy="primary"
                onClick={handleStartReturnClick}
                size="md"
                text={text.startReturnButton}
              />
              {claimsEnabled &&
                (warrantiesEnabled ? (
                  <>
                    <RedoButton
                      disabled={shouldDisableButtons}
                      hierarchy="secondary"
                      IconTrailing={ChevronDownSvg}
                      onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                        setClaimMenuOpen(!claimMenuOpen);
                        setClaimMenuRef(event.currentTarget);
                      }}
                      size="md"
                      text="File a claim"
                    />
                    <RedoCommandMenu
                      anchor={claimMenuRef}
                      items={claimMenuItems}
                      open={claimMenuOpen}
                      setOpen={setClaimMenuOpen}
                    />
                  </>
                ) : (
                  <RedoButton
                    disabled={shouldDisableButtons}
                    hierarchy="secondary"
                    onClick={handleStartClaimClick}
                    size="md"
                    text="File a claim"
                  />
                ))}

              {!hideRegisterButton && registrationsEnabled && (
                <RedoButton
                  disabled={shouldDisableButtons}
                  hierarchy="secondary"
                  onClick={handleRegisterClick}
                  size="md"
                  text={text.registerButton}
                />
              )}
            </>
          )}
        </Flex>
      </Flex>
      <Flex flexDirection="column" gap="none">
        {/* Display Returnable Items First */}
        {returnableItems.map((lineItem: LineItem, index: number) => (
          <MobileLineItemRow
            isEligibleForRegistration={isLineItemEligibleForRegistration(
              lineItem.id.toString(),
            )}
            isFirst={index === 0}
            isLast={false}
            key={lineItem.id}
            lineItem={lineItem}
            onViewReturnClick={handleViewReturnDetailsClick}
            returnableQuantity={getReturnableQuantity(lineItem)}
            showRegistrations={registrationsEnabled}
            style={{ marginTop: "8px" }}
            totalRegistrations={findTotalRegistrationsByLineItemId(
              lineItem.id.toString(),
            )}
          />
        ))}

        {/* Returns loading state */}
        {returnsLoading ? (
          <Flex flexDirection="column" gap="md" p="3xl">
            <AnimatedSkeletonBlock height="20px" width="60%" />
            <AnimatedSkeletonBlock height="40px" width="100%" />
            <AnimatedSkeletonBlock height="40px" width="100%" />
          </Flex>
        ) : (
          /* Display Returns grouped by Return ID */
          Array.from(returnedItemsByReturnId.entries()).map(
            ([returnId, items], returnIndex) => {
              // Get the return status from the corresponding return
              const returnData = returnsByReturnId.get(returnId);
              const returnStatus = returnData?.status || ReturnStatus.COMPLETE;

              const isRejected =
                isReturnStatusDisplayedRejected[returnStatus as ReturnStatus];
              return (
                <Fragment key={`return-${returnId}`}>
                  {/* Return Header */}
                  <Flex
                    align="flex-start"
                    bgColor="primary"
                    flexDirection="column"
                    gap="xs"
                    mt={returnIndex > 0 ? "md" : "none"}
                    p="lg"
                  >
                    <Flex align="center" flexDirection="row" gap="sm">
                      {returnStatus === ReturnStatus.COMPLETE ? (
                        <CheckCircleIcon style={{ color: "#079455" }} />
                      ) : !isRejected ? (
                        <ClockIcon style={{ color: "#F79009" }} />
                      ) : (
                        <XCircleIcon style={{ color: "#d92d20" }} />
                      )}
                      <Text fontSize="sm" fontWeight="semibold">
                        {getReturnHeaderText(returnId)}
                      </Text>
                    </Flex>
                    <Text
                      fontSize="xs"
                      fontWeight="regular"
                      textColor="tertiary"
                    >
                      {getReturnCompletionMessage(returnId)}
                    </Text>
                    {!isRejected && (
                      <Text
                        className={ordersStyles.orderDetailsLink}
                        fontSize="xs"
                        fontWeight="regular"
                        onClick={handleReturnDetailsClick(returnId)}
                        style={{
                          textDecoration: "underline",
                          cursor: "pointer",
                          color: "#3366ff",
                          marginTop: 2,
                        }}
                      >
                        View return details
                      </Text>
                    )}
                  </Flex>

                  {/* Returned Items for this Return */}
                  {items.map(
                    (lineItem: ReturnedLineItem, itemIndex: number) => (
                      <MobileLineItemRow
                        isEligibleForRegistration={isLineItemEligibleForRegistration(
                          lineItem.id.toString(),
                        )}
                        isFirst={itemIndex === 0}
                        isLast={false}
                        key={`line-${lineItem.id}-${returnId}-${itemIndex}`}
                        lineItem={lineItem}
                        quantityLabel={`${lineItem.quantity}`}
                        returnableQuantity={getReturnableQuantity(lineItem)}
                        showRegistrations={registrationsEnabled}
                        totalRegistrations={findTotalRegistrationsByLineItemId(
                          lineItem.id?.toString() || "",
                        )}
                      />
                    ),
                  )}
                </Fragment>
              );
            },
          )
        )}
      </Flex>
    </Flex>
  );
});

const OrderCard = memo(function OrderCard({
  order,
  registerOnly = false,
}: {
  order: Order;
  registerOnly?: boolean;
}) {
  const [claimMenuOpen, setClaimMenuOpen] = useState(false);
  const [claimMenuRef, setClaimMenuRef] = useState<HTMLButtonElement | null>(
    null,
  );

  // Get feature flags
  const registrationsEnabled = useRegistrationsEnabled();
  const claimsEnabled = useClaimsEnabled();
  const warrantiesEnabled = useWarrantiesEnabled();

  const {
    orderName,
    formattedOrderDate,
    orderItems,
    orderSourceDisplayName,
    hideRegisterButton,
    shouldDisableButtons,
    findTotalRegistrationsByLineItemId,
    isLineItemEligibleForRegistration,
    handleRegisterClick,
    handleViewOrderDetailsClick,
    handleStartReturnClick,
    handleStartClaimClick,
    handleStartWarrantyClaimClick,
    handleViewReturnDetailsClick,
    text,
    getReturnCompletionMessage,
    getReturnHeaderText,
    separateLineItems,
    getReturnsByReturnId,
    getReturnedItemsByReturnId,
    getReturnableQuantity,
    returnsLoading,
  } = useOrderCard({ order });

  // Define claim menu items
  const claimMenuItems: RedoCommandMenuItem[] = [
    {
      text: text.startClaimButton,
      onClick: handleStartClaimClick,
      disabled: shouldDisableButtons,
    },
    {
      text: text.startWarrantyClaimButton,
      onClick: handleStartWarrantyClaimClick,
      disabled: shouldDisableButtons,
    },
  ];

  // Group line items by return ID
  const returnsByReturnId = getReturnsByReturnId();

  // Separate returnable from fully returned items
  // Type assertion is needed because there's a mismatch between the LineItem types from different imports
  // The orderItems from useOrderCard has a different LineItem type than what separateLineItems expects
  const orderItemsData = separateLineItems(orderItems as LineItem[]);
  const returnableItems = orderItemsData.returnableItems;

  // Get returned items grouped by return ID
  const returnedItemsByReturnId = getReturnedItemsByReturnId(
    orderItems as LineItem[],
  );

  // Handle click on return details
  const handleReturnDetailsClick = (returnId: string) => () => {
    handleViewReturnDetailsClick(returnId);
  };

  return (
    <Flex
      borderColor="primary"
      borderStyle="solid"
      borderWidth="1px"
      flexDirection="column"
      gap="none"
      overflow="hidden"
      radius="md"
    >
      <Flex bgColor="secondary" flexDirection="column" gap="xs" p="3xl">
        <Flex align="center" flexDirection="row" justify="space-between">
          <Text fontSize="lg" fontWeight="semibold">
            Order {orderName}
          </Text>
          <Flex flexDirection="row" gap="sm">
            {returnsLoading ? (
              <RedoButton
                disabled
                hierarchy="primary"
                text="Loading returns..."
              />
            ) : registerOnly ? (
              !hideRegisterButton &&
              registrationsEnabled && (
                <RedoButton
                  hierarchy="secondary"
                  onClick={handleRegisterClick}
                  text={text.registerButton}
                />
              )
            ) : (
              <>
                {!hideRegisterButton && registrationsEnabled && (
                  <RedoButton
                    disabled={shouldDisableButtons}
                    hierarchy="secondary"
                    onClick={handleRegisterClick}
                    text={text.registerButton}
                  />
                )}
                {claimsEnabled &&
                  (warrantiesEnabled ? (
                    <>
                      <RedoButton
                        disabled={shouldDisableButtons}
                        hierarchy="secondary"
                        IconTrailing={ChevronDownSvg}
                        onClick={(
                          event: React.MouseEvent<HTMLButtonElement>,
                        ) => {
                          setClaimMenuOpen(!claimMenuOpen);
                          setClaimMenuRef(event.currentTarget);
                        }}
                        text="File a claim"
                      />
                      <RedoCommandMenu
                        anchor={claimMenuRef}
                        items={claimMenuItems}
                        open={claimMenuOpen}
                        setOpen={setClaimMenuOpen}
                      />
                    </>
                  ) : (
                    <RedoButton
                      disabled={shouldDisableButtons}
                      hierarchy="secondary"
                      onClick={handleStartClaimClick}
                      text="File a claim"
                    />
                  ))}
                <RedoButton
                  disabled={shouldDisableButtons}
                  hierarchy="primary"
                  onClick={handleStartReturnClick}
                  text={text.startReturnButton}
                />
              </>
            )}
          </Flex>
        </Flex>
        <Flex align="center" flexDirection="row" gap="xs">
          {orderSourceDisplayName && (
            <>
              <Text fontSize="sm" fontWeight="regular" textColor="tertiary">
                {orderSourceDisplayName}
              </Text>
              <DotIcon className={ordersStyles.dotIcon} />
            </>
          )}
          <Text fontSize="sm" fontWeight="regular" textColor="tertiary">
            Ordered: {formattedOrderDate}
          </Text>
          <DotIcon className={ordersStyles.dotIcon} />
          <Text
            className={ordersStyles.orderDetailsLink}
            fontSize="sm"
            fontWeight="regular"
            onClick={handleViewOrderDetailsClick}
            style={{ textDecoration: "underline", cursor: "pointer" }}
          >
            {text.viewOrderDetails}
          </Text>
        </Flex>
      </Flex>

      <Flex flexDirection="column" gap="none">
        {/* Header Row */}
        <LineItemRow registrationsEnabled={registrationsEnabled} />

        {/* Display Returnable Items First */}
        {returnableItems.map((lineItem: LineItem, index: number) => (
          <LineItemRow
            isEligibleForRegistration={isLineItemEligibleForRegistration(
              lineItem.id.toString(),
            )}
            isLast={false}
            key={lineItem.id}
            lineItem={lineItem}
            onViewReturnClick={handleViewReturnDetailsClick}
            registrationsEnabled={registrationsEnabled}
            returnableQuantity={getReturnableQuantity(lineItem)}
            totalRegistrations={findTotalRegistrationsByLineItemId(
              lineItem.id.toString(),
            )}
          />
        ))}

        {/* Returns loading state */}
        {returnsLoading ? (
          <Flex flexDirection="column" gap="md" p="3xl">
            <AnimatedSkeletonBlock height="24px" width="40%" />
            <Flex flexDirection="row" gap="md" mt="sm">
              <AnimatedSkeletonBlock height="18px" width="20%" />
              <AnimatedSkeletonBlock height="18px" width="15%" />
            </Flex>
            <Flex flexDirection="column" gap="lg" mt="2xl">
              <AnimatedSkeletonBlock height="50px" width="100%" />
              <AnimatedSkeletonBlock height="50px" width="100%" />
            </Flex>
          </Flex>
        ) : (
          /* Display Returns grouped by Return ID */
          Array.from(returnedItemsByReturnId.entries()).map(
            ([returnId, items], returnIndex) => {
              // Get the return status from the corresponding return
              const returnData = returnsByReturnId.get(returnId);
              const returnStatus = returnData?.status || ReturnStatus.COMPLETE;

              const isRejected =
                isReturnStatusDisplayedRejected[returnStatus as ReturnStatus];

              return (
                <Fragment key={`return-${returnId}`}>
                  {/* Return Header */}
                  <Flex
                    align="flex-start"
                    bgColor="primary"
                    mt={returnIndex > 0 ? "xs" : "none"}
                    p="3xl"
                    w="full"
                  >
                    <Flex flexDirection="column" gap="xs">
                      <Flex align="center" flexDirection="row" gap="sm">
                        {returnStatus === ReturnStatus.COMPLETE ? (
                          <CheckCircleIcon style={{ color: "#079455" }} />
                        ) : !isRejected ? (
                          <ClockIcon style={{ color: "#F79009" }} />
                        ) : (
                          <XCircleIcon style={{ color: "#d92d20" }} />
                        )}
                        <Text fontSize="sm" fontWeight="semibold">
                          {getReturnHeaderText(returnId)}
                        </Text>
                      </Flex>
                      <Flex align="center" flexDirection="row" gap="md">
                        <Text
                          fontSize="sm"
                          fontWeight="regular"
                          textColor="tertiary"
                        >
                          {getReturnCompletionMessage(returnId)}
                        </Text>
                        {!isRejected && (
                          <Text
                            className={ordersStyles.orderDetailsLink}
                            fontSize="sm"
                            fontWeight="regular"
                            onClick={handleReturnDetailsClick(returnId)}
                            style={{
                              textDecoration: "underline",
                              cursor: "pointer",
                              color: "#3366ff",
                            }}
                          >
                            View return details
                          </Text>
                        )}
                      </Flex>
                    </Flex>
                  </Flex>

                  {/* Returned Items for this Return */}
                  {items.map(
                    (lineItem: ReturnedLineItem, itemIndex: number) => (
                      <LineItemRow
                        isEligibleForRegistration={isLineItemEligibleForRegistration(
                          lineItem.id.toString(),
                        )}
                        isLast={false}
                        key={`line-${lineItem.id}-${returnId}-${itemIndex}`}
                        lineItem={lineItem}
                        quantityLabel={`${lineItem.quantity}`}
                        registrationsEnabled={registrationsEnabled}
                        returnableQuantity={getReturnableQuantity(lineItem)}
                        totalRegistrations={findTotalRegistrationsByLineItemId(
                          lineItem.id?.toString() || "",
                        )}
                      />
                    ),
                  )}
                </Fragment>
              );
            },
          )
        )}
      </Flex>
    </Flex>
  );
});

const LineItemRow = memo(function LineItemRow({
  lineItem,
  isLast,
  totalRegistrations,
  onViewReturnClick,
  registrationsEnabled = true,
  quantityLabel,
  returnableQuantity,
  isEligibleForRegistration,
}: {
  lineItem?: LineItem;
  isLast?: boolean;
  totalRegistrations?: number;
  isEligibleForRegistration?: boolean;
  onViewReturnClick?: (returnId: string) => void;
  registrationsEnabled?: boolean;
  quantityLabel?: string;
  returnableQuantity?: number;
}) {
  // Always call the hook regardless of whether it's a header row
  const args = useLineItemRow({
    lineItem,
    totalRegistrations,
    isEligibleForRegistration,
  });

  // Check if this is a header row (no lineItem)
  if (!lineItem) {
    return (
      <Flex
        align="center"
        borderBottomWidth={isLast ? "none" : "1px"}
        borderColor="primary"
        borderStyle="solid"
        borderTopWidth="1px"
        flexDirection="row"
        gap="xs"
      >
        <Flex px="3xl" py="lg" w="full">
          <Text fontSize="sm" fontWeight="regular" textColor="tertiary">
            Item
          </Text>
        </Flex>
        <Flex className={ordersStyles.nonItemColumn} py="lg">
          <Text fontSize="sm" fontWeight="regular" textColor="tertiary">
            Quantity
          </Text>
        </Flex>
        <Flex className={ordersStyles.nonItemColumn} py="lg">
          <Text fontSize="sm" fontWeight="regular" textColor="tertiary">
            Price
          </Text>
        </Flex>
        {registrationsEnabled && (
          <Flex className={ordersStyles.registeredBadge} py="lg">
            <Text fontSize="sm" fontWeight="regular" textColor="tertiary">
              Registered
            </Text>
          </Flex>
        )}
      </Flex>
    );
  }

  if (args.isHeader) {
    // This shouldn't happen, but we handle it just in case
    return null;
  }

  // Format the quantity display
  let displayQuantity = args.formattedQuantity;

  if (quantityLabel) {
    // If quantityLabel is provided (for returned items), use it
    displayQuantity = quantityLabel;
  } else if (returnableQuantity !== undefined) {
    const totalQty = lineItem.quantity;
    if (returnableQuantity < totalQty) {
      displayQuantity = `${returnableQuantity}`;
    }
  }

  return (
    <Flex
      align="center"
      borderBottomWidth={isLast ? "none" : "1px"}
      borderColor="primary"
      borderStyle="solid"
      borderTopWidth="none"
      flexDirection="row"
      gap="xs"
    >
      <Flex px="3xl" py="lg" w="full">
        {args.itemDetails && (
          <ItemDetail
            hideTextStrings
            isVertical={false}
            item={args.itemDetails}
            size={ItemDetailSize.SMALL}
          />
        )}
      </Flex>
      <Flex className={ordersStyles.nonItemColumn} py="lg">
        <Text fontSize="sm" fontWeight="regular" textColor="tertiary">
          {displayQuantity}
        </Text>
      </Flex>
      <Flex className={ordersStyles.nonItemColumn} py="lg">
        <Text fontSize="sm" fontWeight="regular" textColor="tertiary">
          {args.formattedPrice}
        </Text>
      </Flex>
      {registrationsEnabled && (
        <Flex className={ordersStyles.registeredBadge} py="lg">
          {args.isHeader ? (
            <Text fontSize="sm" fontWeight="regular" textColor="tertiary">
              Registered
            </Text>
          ) : args.registrationStatus === undefined ? (
            <SkeletonText length={5} />
          ) : (
            <RedoBadge
              color={args.registrationStatus.color}
              text={args.registrationStatus.displayText}
            />
          )}
        </Flex>
      )}
    </Flex>
  );
});

const MobileLineItemRow = memo(function MobileLineItemRow({
  lineItem,
  isFirst,
  isLast,
  totalRegistrations,
  isEligibleForRegistration,
  showRegistrations,
  onViewReturnClick,
  quantityLabel,
  returnableQuantity,
  style,
}: {
  lineItem: LineItem;
  isFirst: boolean;
  isLast: boolean;
  totalRegistrations: number;
  isEligibleForRegistration: boolean;
  showRegistrations: boolean;
  onViewReturnClick?: (returnId: string) => void;
  quantityLabel?: string;
  returnableQuantity?: number;
  style?: React.CSSProperties;
}) {
  const args = useLineItemRow({
    lineItem,
    totalRegistrations,
    isEligibleForRegistration,
  });

  if (args.isHeader) {
    return null;
  }

  let displayQuantity = args.formattedQuantity;

  if (quantityLabel) {
    // If quantityLabel is provided (for returned items), use it
    displayQuantity = quantityLabel;
  } else if (returnableQuantity !== undefined) {
    const totalQty = lineItem.quantity;
    if (returnableQuantity < totalQty) {
      displayQuantity = `${returnableQuantity}`;
    }
  }

  return (
    <Flex flexDirection="column" style={style}>
      {showRegistrations && args.registrationStatus !== undefined ? (
        <RedoBadge
          color={args.registrationStatus.color}
          text={args.registrationStatus.displayText}
        />
      ) : showRegistrations ? (
        <SkeletonText length={5} />
      ) : null}
      <Flex align="center" flexDirection="row" gap="md" py="lg">
        {args.itemDetails && (
          <ItemDetail
            hideQuantity
            isVertical={false}
            item={args.itemDetails}
            size={ItemDetailSize.SMALL}
          />
        )}
        <Text fontSize="sm" fontWeight="regular" ml="auto" textColor="tertiary">
          x {displayQuantity}
        </Text>
      </Flex>
      {!isLast && (
        <Flex
          borderBottomWidth="1px"
          borderColor="primary"
          borderStyle="solid"
          w="full"
        />
      )}
    </Flex>
  );
});
